import type { ErrorResponse } from '@diamond/shared/schema';
import type { Auth } from '@diamond/shared/schema/auth';
import { AuthTokenResponseSchema } from '@diamond/shared/schema/auth/token';
import { Value } from '@sinclair/typebox/value';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { UAParser } from 'ua-parser-js';

import {
  BUYER_REFRESH_TIMEOUT_MS,
  changeBuyerSession,
  coordinateBuyerRefresh,
  getBuyerSessionGeneration,
} from './buyer-session-coordinator';
import { CookieService } from './cookieService';
import {
  logSessionRecovery,
  SessionRecoveryEvent,
} from './session-recovery-telemetry';

const validOS = ['windows', 'linux', 'macos', 'android', 'ios'];
const osName = UAParser().os.name?.toLowerCase().replace(/\s/g, '');

const isValidOS = typeof osName === 'string' && validOS.includes(osName);
const clientOS = (() => {
  if (isValidOS) {
    return osName;
  }
  return 'windows';
})();

const clientBrowserName = UAParser().browser.name;
const clientBrowserVersion = UAParser().browser.version;

export const ADDITIONAL_CLIENT_HEADERS = {
  'X-Client-OS': String(clientOS),
  'X-Client-Device': String(clientBrowserName),
  'X-Client-Version': String(clientBrowserVersion),
};

const API_URL = process.env['NX_API_URL'] || '';

export type RefreshResult =
  | { status: 'success'; data: Auth['refreshToken']['response'] }
  | { status: 'invalid-session' }
  | { status: 'discarded' }
  | { status: 'temporary-failure'; uncertain?: boolean };

export class SessionRecoveryError extends AxiosError<ErrorResponse> {
  constructor(error?: AxiosError<unknown>) {
    const message = 'Gagal memulihkan sesi. Silakan coba lagi.';
    const response = error?.response;
    super(
      message,
      'SESSION_RECOVERY_TEMPORARY',
      error?.config,
      undefined,
      response
        ? {
            ...response,
            data: {
              error: 'SESSION_RECOVERY_TEMPORARY',
              message,
              statusCode: response.status,
            },
          }
        : undefined
    );
    this.name = 'SessionRecoveryError';
  }
}

type SessionHandlers = {
  coordinateAcrossTabs?: boolean;
  onRecoveryEvent?: (event: SessionRecoveryEvent) => void;
  isSessionActive: () => boolean;
  shouldRecoverSession: () => boolean;
  onInvalidSession: () => Promise<void>;
  onTokensRefreshed: (data: Auth['refreshToken']['response']) => void;
};

type SessionRecoveryStage = 'cookie' | 'refresh';

let sessionHandlers: SessionHandlers | undefined;
let sessionGeneration = 0;

export function registerSessionHandlers(handlers: SessionHandlers) {
  sessionHandlers = handlers;
  return () => {
    if (sessionHandlers === handlers) sessionHandlers = undefined;
  };
}

function reportRecovery(event: SessionRecoveryEvent) {
  try {
    logSessionRecovery(event);
    sessionHandlers?.onRecoveryEvent?.(event);
  } catch {
    // Diagnostics must not interrupt session recovery.
  }
}

async function invalidateSession(generation?: string, token?: string) {
  if (generation !== undefined) {
    let cleanup: Promise<void> | undefined;
    await changeBuyerSession(
      () => {
        cleanup = sessionHandlers?.onInvalidSession();
      },
      generation,
      token
    );
    await cleanup;
    return;
  }
  sessionGeneration += 1;
  if (sessionHandlers) {
    await sessionHandlers.onInvalidSession();
    return;
  }
  if (typeof window !== 'undefined') {
    CookieService.clearAllAuthCookies();
    window.location.assign('/login');
  }
}

export function classifyRefreshFailure(status?: number): RefreshResult {
  return status === 401
    ? { status: 'invalid-session' }
    : { status: 'temporary-failure' };
}

class TokenManager {
  private refreshPromise: Promise<RefreshResult> | null = null;

  async refreshToken(generation?: string): Promise<RefreshResult> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.recover(generation);
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async recover(buyerGeneration?: string): Promise<RefreshResult> {
    const refreshToken = CookieService.getRefreshToken();
    if (!refreshToken) {
      reportRecovery({ name: 'refresh_missing_credentials' });
      return { status: 'invalid-session' };
    }
    const generation = sessionGeneration;
    const commit = (data: Auth['refreshToken']['response']) => {
      if (sessionHandlers?.isSessionActive() === false)
        throw new Error('Session ended');
      CookieService.setAuthTokens(data.token, data.refresh_token);
      sessionHandlers?.onTokensRefreshed(data);
    };
    if (buyerGeneration !== undefined) {
      const result = await coordinateBuyerRefresh(
        buyerGeneration,
        refreshToken,
        () => this.performRefresh(refreshToken),
        commit
      );
      if (result.status === 'discarded')
        reportRecovery({ name: 'refresh_discarded' });
      if (result.status === 'temporary-failure')
        reportRecovery({ name: 'refresh_unavailable' });
      return result;
    }
    const result = await this.performRefresh(refreshToken);
    if (
      generation !== sessionGeneration ||
      sessionHandlers?.isSessionActive() === false
    ) {
      reportRecovery({ name: 'refresh_discarded' });
      return { status: 'discarded' };
    }
    if (result.status === 'success') commit(result.data);
    return result;
  }

  private async performRefresh(refreshToken: string): Promise<RefreshResult> {
    reportRecovery({ name: 'refresh_started' });

    const isAdmin =
      typeof window !== 'undefined' &&
      (window.location.hostname.includes('admin') ||
        window.location.pathname.includes('admin') ||
        window.location.host.includes(':4300'));
    const body: Auth['refreshToken']['body'] = {
      refresh_token: refreshToken,
      subscriber: isAdmin ? 'web_admin' : 'web_buyer',
    };

    try {
      const { data } = await axios.post<unknown>(
        `${API_URL}/auth/refresh-token`,
        body,
        {
          timeout: BUYER_REFRESH_TIMEOUT_MS,
          headers: {
            ...ADDITIONAL_CLIENT_HEADERS,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!Value.Check(AuthTokenResponseSchema, data)) {
        reportRecovery({ name: 'refresh_invalid_response' });
        return { status: 'temporary-failure', uncertain: true };
      }
      reportRecovery({ name: 'refresh_succeeded' });
      return { status: 'success', data };
    } catch (error) {
      const isHttpError = axios.isAxiosError<unknown>(error);
      const httpStatus = isHttpError ? error.response?.status : undefined;
      let reason: SessionRecoveryEvent['reason'] = 'unexpected';
      if (isHttpError) reason = httpStatus === undefined ? 'transport' : 'http';
      reportRecovery({
        name: httpStatus === 401 ? 'refresh_rejected' : 'refresh_failed',
        httpStatus,
        reason,
      });
      return httpStatus === undefined
        ? { status: 'temporary-failure', uncertain: true }
        : classifyRefreshFailure(httpStatus);
    }
  }
}

// Single instance of token manager
const tokenManager = new TokenManager();

export async function recoverBuyerSession(): Promise<RefreshResult> {
  if (sessionHandlers?.isSessionActive() === false)
    return { status: 'discarded' };
  try {
    const generation = await getBuyerSessionGeneration();
    const result = await tokenManager.refreshToken(generation);
    if (result.status === 'invalid-session')
      await invalidateSession(generation);
    return result;
  } catch {
    return { status: 'temporary-failure' };
  }
}

/**
 * Handles request errors with consistent logging
 */
function handleRequestError(error: AxiosError): Promise<never> {
  console.error('[API Request Error]:', {
    message: error.message,
    code: error.code,
    url: error.config?.url,
    method: error.config?.method,
  });
  return Promise.reject(error);
}

/**
 * Handles successful responses
 */
function handleSuccessResponse(
  response: AxiosResponse,
  axiosInstance: ReturnType<typeof axios.create>
): AxiosResponse | Promise<AxiosResponse> {
  if (response.status === 0) {
    return handleResponseError(
      new AxiosError(
        'Network Error',
        AxiosError.ERR_NETWORK,
        response.config,
        response.request
      ),
      axiosInstance
    );
  }

  return response;
}

/**
 * Handles 401 unauthorized errors with token refresh
 */
async function handleUnauthorizedError(
  error: AxiosError,
  axiosInstance: ReturnType<typeof axios.create>
): Promise<AxiosResponse | never> {
  const originalRequest = error.config;

  if (
    typeof window === 'undefined' ||
    !originalRequest ||
    originalRequest.url?.startsWith('/auth/')
  ) {
    return Promise.reject(error);
  }
  if (sessionHandlers?.shouldRecoverSession() === false) {
    return Promise.reject(error);
  }
  const buyerGeneration = originalRequest._buyerSessionGeneration;
  if (
    buyerGeneration !== undefined &&
    buyerGeneration !== (await getBuyerSessionGeneration())
  ) {
    throw new SessionRecoveryError(error);
  }
  if (originalRequest._sessionRecoveryStage === 'refresh') {
    const current = CookieService.getJwtToken();
    if (
      buyerGeneration !== undefined &&
      current &&
      originalRequest.headers.Authorization !== `Bearer ${current}`
    ) {
      throw new SessionRecoveryError(error);
    }
    reportRecovery({ name: 'retry_rejected', httpStatus: 401 });
    await invalidateSession(
      buyerGeneration,
      String(originalRequest.headers.Authorization)
    );
    return Promise.reject(error);
  }
  const currentToken = CookieService.getJwtToken();
  if (
    originalRequest._sessionRecoveryStage === undefined &&
    currentToken &&
    originalRequest.headers.Authorization !== `Bearer ${currentToken}`
  ) {
    originalRequest._sessionRecoveryStage = 'cookie';
    originalRequest.headers.Authorization = `Bearer ${currentToken}`;
    return axiosInstance(originalRequest);
  }
  originalRequest._sessionRecoveryStage = 'refresh';
  const result = await tokenManager.refreshToken(buyerGeneration);
  switch (result.status) {
    case 'success':
      originalRequest.headers.Authorization = `Bearer ${result.data.token}`;
      return axiosInstance(originalRequest);
    case 'invalid-session':
      await invalidateSession(
        buyerGeneration,
        String(originalRequest.headers.Authorization)
      );
      break;
    case 'discarded':
    case 'temporary-failure':
      throw new SessionRecoveryError(error);
  }

  return Promise.reject(error);
}

/**
 * Handles response errors with consistent logging
 */
async function handleResponseError(
  error: AxiosError,
  axiosInstance: ReturnType<typeof axios.create>
): Promise<AxiosResponse | never> {
  // Log non-401 errors for debugging
  if (error.response?.status !== 401) {
    console.error('[API Response Error]:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
    });
  }

  if (error.response?.status === 401) {
    return handleUnauthorizedError(error, axiosInstance);
  }

  return Promise.reject(error);
}

function getContentType(config?: AxiosRequestConfig<unknown>) {
  const contentType: Record<string, string> =
    (config?.method ?? '').toUpperCase() === 'DELETE'
      ? {}
      : { 'Content-Type': 'application/json' };
  return contentType;
}

function getTimeoutConfig(): AxiosRequestConfig {
  return {
    timeout: 120_000,
  };
}

/**
 * Gets the best available token (cookie first, then fallback)
 */
function getBestToken(fallbackToken?: string | null): string | null {
  if (typeof window === 'undefined') return fallbackToken || null;

  const cookieToken = CookieService.getJwtToken();
  const isLoginPage = window.location.pathname === '/login';
  if (isLoginPage) return fallbackToken || null;

  return cookieToken || fallbackToken || null;
}

/**
 * Creates an API instance with automatic token refresh
 *
 * @param token Bearer token (optional)
 * @param config AxiosRequestConfig (optional)
 * @param baseURL Additional base url options (optional)
 */
export function apiInstance(
  token?: string | null,
  config?: AxiosRequestConfig<unknown>,
  baseURL = API_URL
) {
  const axiosInstance = axios.create({
    baseURL,
    headers: {
      ...ADDITIONAL_CLIENT_HEADERS,
      ...config?.headers,
      ...getContentType(config),
    },
    ...getTimeoutConfig(),
    ...config,
  });

  axiosInstance.interceptors.request.use(async (config) => {
    if (config.url?.startsWith('/auth/')) {
      const authToken = getBestToken(token);
      if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
      return config;
    }
    if (sessionHandlers?.isSessionActive() === false)
      throw new SessionRecoveryError();
    if (
      sessionHandlers?.coordinateAcrossTabs &&
      sessionHandlers.shouldRecoverSession()
    ) {
      try {
        const generation = await getBuyerSessionGeneration();
        if (
          config._buyerSessionGeneration !== undefined &&
          config._buyerSessionGeneration !== generation
        ) {
          throw new SessionRecoveryError();
        }
        config._buyerSessionGeneration = generation;
      } catch {
        throw new SessionRecoveryError();
      }
    }
    if (config._sessionRecoveryStage) return config;
    const tokenToUse = getBestToken(token);
    if (tokenToUse) {
      config.headers.Authorization = `Bearer ${tokenToUse}`;
    }
    return config;
  }, handleRequestError);

  axiosInstance.interceptors.response.use(
    (response) => handleSuccessResponse(response, axiosInstance),
    (error: AxiosError) => handleResponseError(error, axiosInstance)
  );

  return axiosInstance;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    _sessionRecoveryStage?: SessionRecoveryStage;
    _buyerSessionGeneration?: string;
  }
}
